import React, { useEffect, useState, useImperativeHandle} from 'react';

import { Form, InputGroup } from "react-bootstrap";
import { ValidationMessages } from 'Utils/Constants';

const isEmpty = (value) => {
  return value === undefined ||
    value === null ||
    /^\s*$/.test(value);
}

/**
 *
 * @param {FormField.defaultProps} props
 * @returns
 */
const FormField = ((props, ref) => {
   const { fieldName } = { fieldName: 'formField', ...props };
   const { label } = { label: 'Field Label', ...props };
   const { startValue } = { startValue: '', ...props };
   const { required } = { required: false, ...props };
   const { allowedCharacters } = { allowedCharacters: undefined, ...props };
   const { validators } = { validators: [], ...props };
   const { placeholder } = { placeholder: '', ...props };
   const { onValueChanged } = { onValueChanged: function (fieldName, fieldValue, isValid) { console.warn('FormField onValueChanged not set.', arguments) }, ...props };
   const { disabled } = { disabled: false, ...props };
   const { renderControl } = { renderControl: DefaultControl, ...props };
  const [showError, setShowError] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const [fieldValue, setFieldValue] = useState(startValue);

  // Run validation on initial value
  useEffect(() => {
    onChange(startValue);
    if (isEmpty(startValue)) {
      // We don't want to see errors if the form hasn't been filled out.
      setShowError(false);
    }
  }, [startValue]);

  // Expose setFieldValue to parent via ref. Useful for causing form field updates externally.
  useImperativeHandle(ref, () => ({
    setFieldValue: (val) => {
      setFieldValue(val);
      if (val === undefined) {
        setShowError(false);
        setErrorMessages([]);
      }
    }
  }));

  const multiValidate = (value) => {
    if (!validators || validators.length === 0) return true;
    let errors = [];
    validators.forEach(validator => {
      let valid = true;
      if (validator.action) {
        valid = validator.action(value);
      } else if (validator.regex) {
        valid = validator.regex.test(value);
      }
      if (!valid) {
        errors.push(validator.errorMessage ?? 'Invalid value.');
      }
    });
    setErrorMessages(errors);
    return errors.length === 0;
  };

  const isValid = (value) => {
    // Short circuit if required but empty.
    if (required === true && isEmpty(value)) {
      setErrorMessages([ValidationMessages.REQUIRED]);
      return false;
    }

    // Run independent validators if there's a value.
    if (!isEmpty(value))
    {
      return multiValidate(value);
    }

    // Otherwise field is optional and has no value.
    return true;
  };

  const isInvalidCharacter = (value) => {
    if (allowedCharacters && value.length > 0) {
      const lastChar = value[value.length - 1];
      const valid = allowedCharacters.test(lastChar);
      if (valid === false)
      {
        setErrorMessages([`Invalid character ${lastChar}`]);
        setShowError(true);
        return true;
      }
    }
    return false;
  }

  const onChange = (value) => {
    if (isInvalidCharacter(value)) return; // Prevent entry of disallowed characters.
    const valid = isValid(value);
    setShowError(!valid);
    setFieldValue(value);
    if (typeof onValueChanged === 'function') {
      onValueChanged(fieldName, value, valid);
    }
  }

  const onBlur = (val) => {
    isValid(val) ? setShowError(false) : setShowError(true);
  }

  // Don't show placeholder when disabled
  const placeholderData = disabled ? '' : placeholder;
  const controlProps = { ...props, placeholderData };

  return (
    <Form.Group className="mb-3" controlId={fieldName}>
      <Form.Label>{label}</Form.Label>
      {renderControl(controlProps, showError, fieldValue, onChange, onBlur)}
      {showError && <div className='invalid-feedback d-block' role='alert'>
        {errorMessages.join(' ')}
      </div>}
    </Form.Group>
  );
});

const DefaultControl = (props, showError, fieldValue, onChange, onBlur) =>
<InputGroup>
    {props.prepend && <div className="input-group-prepend">
      <InputGroup.Text className={showError && 'redBorder'} id="basic-addon1">{props.prepend}</InputGroup.Text>
    </div>}
    <Form.Control
      className={showError && 'is-invalid'}
      name={props.fieldName}
      value={fieldValue || ''}
      maxLength={props.maxLength}
      onChange={e => onChange(e.target.value)}
      onBlur={e => onBlur(e.target.value)}
      disabled={props.disabled}
      placeholder={props.placeholder}
      type={props.type}
      autoComplete={props.autoComplete}
      aria-describedby="basic-addon2"
    />
    {props.append && <div className="input-group-append">
      <InputGroup.Text className={showError && 'redBorder'} id="basic-addon2"><span dangerouslySetInnerHTML={{__html: props.append}}></span></InputGroup.Text>
      </div>}
  </InputGroup>;


export default FormField;